import { defineStore } from 'pinia'
import { ref } from 'vue'
import type {
  StaffFileProcessingResponse,
  UserIncidentFootage,
  UserIncidentImage,
  ViewIncidentFileResponse
} from '@roadfootage/api-client'
import { useApi } from '../../composables/api'

export type MediaKind = 'video' | 'image'

/**
 * Media of the incident report open in the Incident Reports list modal (RF-128): preview urls,
 * pipeline progress, and the removals made while it is open. Separate from the moderation store,
 * which owns the review queue.
 */
export const useIncidentMediaStore = defineStore('incident-media', () => {
  const {
    adminIncidentReportsApi,
    adminIncidentFootageApi,
    adminIncidentImagesApi,
    adminUserFilesApi
  } = useApi()

  const reportId = ref<string | null>(null)
  const urlsByFileId = ref<Record<string, ViewIncidentFileResponse>>({})
  const processingByFileId = ref<Record<string, StaffFileProcessingResponse>>({})
  const removedIds = ref<string[]>([])
  const loadingUrls = ref(false)

  function reset() {
    reportId.value = null
    urlsByFileId.value = {}
    processingByFileId.value = {}
    removedIds.value = []
    loadingUrls.value = false
  }

  async function loadUrl(id: string, forReport: string, kind: MediaKind) {
    try {
      const response =
        kind === 'video'
          ? await adminIncidentFootageApi.adminIncidentFootageGetForDownloadById({ id })
          : await adminIncidentImagesApi.adminIncidentImagesGetForDownloadById({ id })
      // Ignore a late answer for a report that is no longer open.
      if (reportId.value !== forReport) return
      urlsByFileId.value[id] = response
    } catch (e) {
      // The card shows its placeholder.
      console.error(`Error fetching preview for file ${id}:`, e)
    }
  }

  async function open(id: string, footage: UserIncidentFootage[], images: UserIncidentImage[]) {
    reset()
    reportId.value = id
    loadingUrls.value = true
    await Promise.all([
      refreshProcessing(),
      ...footage.map((f) => loadUrl(f.id!, id, 'video')),
      ...images.map((f) => loadUrl(f.id!, id, 'image'))
    ])
    if (reportId.value === id) loadingUrls.value = false
  }

  async function refreshProcessing() {
    const id = reportId.value
    if (!id) return
    try {
      const files = await adminIncidentReportsApi.adminIncidentReportsGetProcessingById({ id })
      // Ignore a late answer for a report that is no longer open.
      if (reportId.value !== id) return
      processingByFileId.value = Object.fromEntries(files.map((f) => [f.fileId!, f]))
    } catch (e) {
      console.error(`Error fetching processing detail for incident ${id}:`, e)
    }
  }

  /** Soft delete through the generic admin DELETE. Rethrows so the caller can tell staff. */
  async function remove(fileId: string, kind: MediaKind) {
    if (kind === 'video') {
      await adminIncidentFootageApi.adminIncidentFootageDeleteById({ id: fileId })
    } else {
      await adminIncidentImagesApi.adminIncidentImagesDeleteById({ id: fileId })
    }
    removedIds.value.push(fileId)
  }

  async function reprocess(fileId: string) {
    await adminUserFilesApi.adminUserFilesReprocessFileById({ id: fileId })
  }

  return {
    reportId,
    urlsByFileId,
    processingByFileId,
    removedIds,
    loadingUrls,
    open,
    refreshProcessing,
    remove,
    reprocess,
    reset
  }
})
